import {type Line, type Point} from '../../../util/plotHelpers/types';

/**
 * One line or band edge for uPlot to draw.
 * - 'primary-series': A line, including a line displayed by Show Original.
 * - 'band-min': The lower of `y` and `y0` along a min/max band's lower edge.
 * - 'band-max': The higher of `y` and `y0` along a min/max band's upper edge.
 */
export type SeriesEntry = {
  line: Line;
  type: 'primary-series' | 'band-min' | 'band-max';
};

type SeriesIndices = {
  bandMinSeriesIndex?: number;
  bandMaxSeriesIndex?: number;
  primarySeriesIndex?: number;
};

type LineMetricMap = Map<
  string, // metric name
  SeriesIndices
>;

export type LineNameMap = Map<
  string, // line name
  LineMetricMap
>;

/**
 * The drawing order and the positions of each run and metric's main line and band.
 * Position zero is reserved by uPlot. Each band's minimum and maximum edges come
 * next to each other, before the lines that draw over them. Show Original lines
 * are drawn too, but do not replace the main line in the run/metric lookup.
 * Chart data and drawing settings use this same order so they refer to the same lines.
 */
export type UPlotSeriesLayout = {
  seriesEntries: (SeriesEntry | null)[];
  lineNameMap: LineNameMap;
};

type LineWithSeriesIdentity = Line & {
  metricName: string;
  name: string;
};

// Full-fidelity bands store the minimum in `y` and the maximum in `y0`;
// sampled bands store them the other way round. uPlot only fills a band where
// its upper edge is above its lower edge, so read whichever value is lower or
// higher. The values are returned unchanged because `y` can be a non-finite
// string at runtime.

/** The Y value along a band point's lower edge. */
export function getBandLowerY(point: Point): number {
  return point.y0 != null && point.y0 < point.y ? point.y0 : point.y;
}

/** The Y value along a band point's upper edge, or null if the point has none. */
export function getBandUpperY(point: Point): number | null {
  if (point.y0 == null) {
    return null;
  }
  return point.y0 < point.y ? point.y : point.y0;
}

/** Area-shaped supporting data supplies the minimum and maximum edges of a band. */
function isUPlotBandSourceLine(line: Line): boolean {
  return line.aux === true && line.meta.type === 'area';
}

/** A run name and metric name let us connect a line with its min/max band. */
function hasSeriesIdentity(line: Line): line is LineWithSeriesIdentity {
  return line.name != null && line.metricName != null;
}

/** Choose the main line for a run and metric, excluding supporting data such as Show Original. */
function isCanonicalPrimarySeriesLine(
  line: Line
): line is LineWithSeriesIdentity {
  return line.aux !== true && hasSeriesIdentity(line);
}

/** List band edges before lines so min/max fills draw behind the lines they surround. */
export function getSeriesEntries(lines: Line[]): UPlotSeriesLayout {
  // uPlot reserves position zero; the first line or band edge starts at position one.
  const entries: (SeriesEntry | null)[] = [null];

  // Supporting data (`aux`) can be a band or a Show Original line.
  // Only area-shaped data becomes a band's two edges; Show Original data
  // still draws as a line, even though it is not the run's main line.
  const primarySeriesLines: Line[] = [];
  const bandSourceLines: Line[] = [];
  for (const line of lines) {
    if (isUPlotBandSourceLine(line)) {
      // Skip absent bands before assigning any series indices.
      if (line.data.length === 0) {
        continue;
      }
      bandSourceLines.push(line);
    } else {
      primarySeriesLines.push(line);
    }
  }

  const lineNameMap = new Map<string, LineMetricMap>();
  for (const line of bandSourceLines) {
    if (line.name != null && line.metricName != null) {
      let lineMetricMap = lineNameMap.get(line.name);
      if (!lineMetricMap) {
        lineMetricMap = new Map<string, SeriesIndices>();
      }
      lineMetricMap.set(line.metricName, {
        bandMinSeriesIndex: entries.length,
        bandMaxSeriesIndex: entries.length + 1,
      });
      lineNameMap.set(line.name, lineMetricMap);
    }
    entries.push({type: 'band-min', line});
    entries.push({
      type: 'band-max',
      line: line,
    });
  }

  // Draw lines over the bands. Record each run and metric's main line in the
  // lookup, while keeping Show Original lines separate from that main line.
  for (const line of primarySeriesLines) {
    if (isCanonicalPrimarySeriesLine(line)) {
      let lineMetricMap = lineNameMap.get(line.name);
      if (!lineMetricMap) {
        lineMetricMap = new Map<string, SeriesIndices>();
      }
      const lineNameMapEntry = lineMetricMap.get(line.metricName);

      lineMetricMap.set(line.metricName, {
        ...lineNameMapEntry,
        primarySeriesIndex: entries.length,
      });
      lineNameMap.set(line.name, lineMetricMap);
    }
    entries.push({type: 'primary-series', line});
  }

  return {seriesEntries: entries, lineNameMap};
}
